import { Flex, Image, Typography } from "antd";

import dayjs from "dayjs";

import utc from "dayjs/plugin/utc";

import timezone from "dayjs/plugin/timezone";

import { formatCompactAmount, toCapitalize } from "../../shared";
import { can } from "../../Auth";

dayjs.extend(utc);

dayjs.extend(timezone);

const START_HOUR = 0;

const END_HOUR = 23;

const ROW_HEIGHT = 64;

const TOTAL_HEIGHT = (END_HOUR - START_HOUR) * ROW_HEIGHT;

const EVENT_HEIGHT = 52;
const DEFAULT_DURATION_MIN = 60; // assumed duration when event has no end

const { Paragraph } = Typography;

const toLocal = (dateStr) => dayjs.utc(dateStr).local();

const formatHour = (h) => {
  const period = h < 12 ? "AM" : "PM";
  const display = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${String(display).padStart(2, "0")}:00 ${period}`;
};

// ✅ Assign columns to overlapping events so they render side-by-side
// ✅ Assign vertical slot index to overlapping events
const computeLayout = (events) => {
  const items = events
    .map((e) => {
      const start = toLocal(e.start);
      const end = e.end
        ? toLocal(e.end)
        : start.add(DEFAULT_DURATION_MIN, "minute");
      return {
        event: e,
        startMs: start.valueOf(),
        endMs: end.valueOf(),
      };
    })
    .sort((a, b) => a.startMs - b.startMs || a.endMs - b.endMs);

  const layoutMap = new Map();
  // Track active events (still ongoing) to determine slot index
  const active = []; // array of { endMs }

  items.forEach((it) => {
    // Remove events that have ended before this one starts
    for (let i = active.length - 1; i >= 0; i--) {
      if (active[i].endMs <= it.startMs) active.splice(i, 1);
    }
    // This event's vertical slot = number of currently active overlapping events
    const slot = active.length;
    layoutMap.set(it.event, { slot });
    active.push({ endMs: it.endMs });
  });

  return layoutMap;
};

const CustomDayView = ({ date, events = [], onSelectEvent }) => {
  const hours = Array.from(
    { length: END_HOUR - START_HOUR + 1 },
    (_, i) => START_HOUR + i,
  );

  const dayEvents = events.filter((e) =>
    toLocal(e.start).isSame(dayjs(date), "day"),
  );

  const V_GAP = 6;
  const EVENT_PADDING = 8;

  // ✅ Group events by hour
  const eventsByHour = {};
  dayEvents.forEach((e) => {
    const h = toLocal(e.start).hour();
    if (!eventsByHour[h]) eventsByHour[h] = [];
    eventsByHour[h].push(e);
  });

  // ✅ Dynamic row height based on event count in that hour
  const getRowHeight = (hour) => {
    const count = eventsByHour[hour]?.length || 0;
    if (count <= 1) return ROW_HEIGHT; // 64px default
    return count * EVENT_HEIGHT + (count - 1) * V_GAP + EVENT_PADDING * 2;
  };

  return (
    <div className="custom-day-view">
      {/* Header */}
      <div className="cdv-header">
        <div className="cdv-weekday">
          {dayjs(date).format("dddd").toUpperCase()}
        </div>
        <div className="cdv-date">{dayjs(date).format("MMMM D, YYYY")}</div>
        <div className="cdv-count">
          {dayEvents.length} session{dayEvents.length !== 1 ? "s" : ""}{" "}
          scheduled
        </div>
      </div>

      {/* Timeline — flow layout, no fixed height, no overflow hidden */}
      <div
        className="cdv-timeline"
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {hours.map((h) => {
          const rowHeight = getRowHeight(h);
          const hourEvents = eventsByHour[h] || [];

          return (
            <div
              key={h}
              className="cdv-row"
              style={{
                position: "relative",
                height: rowHeight,
                minHeight: rowHeight,
                display: "flex",
                alignItems: "flex-start",
                borderBottom: "1px solid #f0f0f0",
              }}
            >
              <div
                className="cdv-time"
                style={{
                  width: 70,
                  flexShrink: 0,
                  paddingTop: 6,
                }}
              >
                {formatHour(h)}
              </div>

              {/* Events container for this hour */}
              <div
                style={{
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                  gap: V_GAP,
                  padding: `${EVENT_PADDING}px 8px`,
                }}
              >
                {hourEvents.map((event) => {
                  const localTime = toLocal(event.start).format("hh:mm A");
                  const bg =
                    event?.status === "pending"
                      ? "#FFA500"
                      : event?.status === "completed"
                        ? "#52c41a"
                        : "#f5222d";

                  return (
                    <div
                      key={event.id}
                      className="cdv-event"
                      style={{
                        background: bg,
                        height: EVENT_HEIGHT,
                        position: "relative",
                        top: 0,
                        left: 0,
                        right: 0,
                        width: "100%",
                      }}
                      onClick={() => onSelectEvent?.(event)}
                    >
                      <Flex vertical gap={2} className="w-100">
                        <Flex justify="space-between" className="w-100" gap={5}>
                          <span className="cdv-event-title">
                            {localTime}
                            <Paragraph
                              className="m-0 fs-12 text-white"
                              ellipsis={{ rows: 1 }}
                              style={{
                                width: "max-content",
                                textAlign: "left",
                                display: "inline",
                              }}
                            >
                              {toCapitalize(event.title)}
                            </Paragraph>
                          </span>
                          {can(
                            "Session Calendar Views",
                            "view payment details",
                          ) && (
                            <>
                              {event?.payment?.advanceamount && (
                                <span className="cdv-event-amount">
                                  {formatCompactAmount(
                                    event?.displayAmount?.toLocaleString(),
                                  )}{" "}
                                  (PKR)
                                </span>
                              )}
                            </>
                          )}
                        </Flex>
                        <Flex
                          justify="space-between"
                          align="flex-start"
                          gap={5}
                        >
                          <Flex gap={2}>
                            <Paragraph
                              className="m-0 fs-10 text-white"
                              ellipsis={{ rows: 1 }}
                              style={{
                                width: "max-content",
                                textAlign: "left",
                                padding: "1px 7px",
                                borderRadius: 4,
                                border: "1px solid #d9d9d9",
                              }}
                            >
                              {toCapitalize(event?.alldata?.service?.name)}
                            </Paragraph>
                          </Flex>
                          {event?.alldata?.appointment?.client_info
                            ?.medical_info &&
                            Object.entries(
                              event?.alldata?.appointment?.client_info
                                ?.medical_info,
                            ).filter(
                              ([key, value]) =>
                                key.startsWith("is") && value === "1",
                            ).length > 0 && (
                              <Image
                                src={
                                  event?.status === "cancel"
                                    ? "/assets/images/issue-o.webp"
                                    : "/assets/images/issue-r.webp"
                                }
                                width={5}
                                height={"auto"}
                                preview={false}
                              />
                            )}
                        </Flex>
                      </Flex>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

CustomDayView.range = (date) => [dayjs(date).startOf("day").toDate()];

CustomDayView.navigate = (date, action) => {
  switch (action) {
    case "PREV":
      return dayjs(date).subtract(1, "day").toDate();
    case "NEXT":
      return dayjs(date).add(1, "day").toDate();
    default:
      return date;
  }
};

CustomDayView.title = (date) => dayjs(date).format("MMMM D, YYYY");

export { CustomDayView };
